import { config } from '@abyss/web/tools/config';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCustomQuery } from '../useCustomQuery';
import EXPORT_DIRECTORY_PDF from './DirectoryExportPdf.graphql';
import type {
  DirectoryExportPdfRequest,
  DirectoryExportPdfResponse,
  UseDirectoryExportPdfResult,
} from './types';

interface UseDirectoryExportPdfOptions {
  onSuccess?: (data: DirectoryExportPdfResponse) => void;
  onError?: (error: Error) => void;
}

const getDirectoryExportPdfResponse = (
  result: any
): DirectoryExportPdfResponse | null => {
  if (result?.data?.exportDirectoryPdf) {
    return result.data.exportDirectoryPdf;
  }

  if (result?.exportDirectoryPdf) {
    return result.exportDirectoryPdf;
  }

  if (result?.pdfBase64) {
    return result;
  }

  return null;
};

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error('PDF export failed');

export const useDirectoryExportPdf = (
  options: UseDirectoryExportPdfOptions = {}
): UseDirectoryExportPdfResult => {
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<DirectoryExportPdfResponse | null>(null);

  const [, mutate] = useCustomQuery(EXPORT_DIRECTORY_PDF, {
    url: config('GRAPHQL_API_URL'),
    accessor: 'exportDirectoryPdf',
    headers: {
      'Accept-Language': i18n.language || 'en',
    },
  });

  const exportPdf = async (params: DirectoryExportPdfRequest) => {
    setLoading(true);
    setError(null);

    try {
      const result = await mutate({ variables: params });
      const responseData = getDirectoryExportPdfResponse(result);

      if (!responseData?.pdfBase64) {
        throw new Error('PDF export response did not include PDF data');
      }

      setData(responseData);
      options?.onSuccess?.(responseData);

      return responseData;
    } catch (err) {
      const nextError = toError(err);

      setError(nextError);
      options?.onError?.(nextError);

      throw nextError;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    data,
    exportPdf,
  };
};
